import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md border-b border-background-200/60'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link
            to="/"
            className="flex items-center gap-2 group whitespace-nowrap"
          >
            <span className="font-heading text-xl md:text-2xl font-bold tracking-tight text-foreground-900">
              GTBuy<span className="text-primary-500">Sheet</span>
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            {/* Category Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'bg-background-100 text-foreground-700 hover:bg-background-200'
                    : 'bg-white/15 backdrop-blur-sm text-white hover:bg-white/25'
                }`}
              >
                <i className="ri-grid-line w-4 h-4 flex items-center justify-center"></i>
                Categories
                <i
                  className={`ri-arrow-down-s-line w-4 h-4 flex items-center justify-center transition-transform duration-200 ${
                    dropdownOpen ? 'rotate-180' : ''
                  }`}
                ></i>
              </button>

              {dropdownOpen && (
                <div className="absolute top-full mt-2 left-0 w-64 bg-background-50 rounded-xl border border-background-200/70 overflow-hidden animate-[fadeIn_0.15s_ease-out]">
                  <div className="max-h-80 overflow-y-auto py-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.name}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors whitespace-nowrap"
                        onClick={() => setDropdownOpen(false)}
                      >
                        <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0"></span>
                        {cat.name}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Nav Links */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className={`px-4 py-2 text-sm font-medium transition-colors whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-700 hover:text-primary-600'
                  : 'text-white/90 hover:text-white'
              }`}
            >
              Spreadsheet
            </a>

            {/* CTA Buttons */}
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-5 py-2 rounded-full text-sm font-semibold border-2 border-primary-400 text-primary-600 hover:bg-primary-50 transition-all whitespace-nowrap cursor-pointer"
            >
              GTBuy Sheet
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-6 py-2 rounded-full text-sm font-semibold bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-all whitespace-nowrap cursor-pointer"
            >
              Buy It Now
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className={`md:hidden w-10 h-10 flex items-center justify-center rounded-lg transition-colors cursor-pointer ${
              scrolled
                ? 'text-foreground-700 hover:bg-background-100'
                : 'text-white hover:bg-white/10'
            }`}
          >
            <i
              className={`w-5 h-5 flex items-center justify-center ${
                mobileMenuOpen ? 'ri-close-line' : 'ri-menu-line'
              }`}
            ></i>
          </button>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden bg-background-50 border-t border-background-200/60 animate-[fadeIn_0.15s_ease-out]">
            <div className="px-4 py-4 space-y-1">
              <p className="px-2 py-1 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
                Categories
              </p>
              {categories.map((cat) => (
                <a
                  key={cat.name}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="block px-3 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 rounded-lg transition-colors"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  {cat.name}
                </a>
              ))}
              <div className="pt-3 space-y-2">
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full text-center px-4 py-2.5 rounded-full text-sm font-semibold border-2 border-primary-400 text-primary-600 hover:bg-primary-50 transition-all"
                >
                  GTBuy Sheet
                </a>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="block w-full text-center px-4 py-2.5 rounded-full text-sm font-semibold bg-foreground-900 text-background-50 hover:bg-foreground-800 transition-all"
                >
                  Buy It Now
                </a>
              </div>
            </div>
          </div>
        )}
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-4px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </nav>
  );
}